import { alpha, createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import { getColor } from '../../theme/styles';

const tokens = (theme: Theme) => getColor(theme.palette.type === 'dark');

export const useReportStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialogPaper: {
      maxWidth: 640,
      overflowX: 'hidden',
    },
    titleRoot: {
      padding: '24px 32px 0',
      '& $title': { fontSize: 20, lineHeight: '28px', fontWeight: 600 },
      '& $subtitle': { fontSize: 14, lineHeight: '20px', marginTop: 4 },
    },
    title: {},
    subtitle: {},
    steps: {
      display: 'flex',
      gap: 6,
      padding: '16px 32px 0',
    },
    stepBar: {
      flex: 1,
      height: 4,
      borderRadius: 3,
      backgroundColor: theme.palette.divider,
    },
    stepBarDone: {
      backgroundColor: theme.palette.primary.main,
    },
    content: {
      padding: '20px 32px',
      minHeight: 240,
    },
    actions: {
      padding: '16px 32px',
      borderTop: `1px solid ${theme.palette.divider}`,
      justifyContent: 'space-between',
      gap: 12,
    },
    fieldLabel: {
      fontSize: 14,
      fontWeight: 600,
      marginBottom: 8,
      color: theme.palette.text.primary,
    },
    hint: {
      fontSize: 13,
      lineHeight: '18px',
      color: theme.palette.text.secondary,
    },
    // Bordered, clickable option card (report type, analysis, field set)
    optionCard: {
      display: 'flex',
      gap: 12,
      alignItems: 'flex-start',
      padding: '14px 16px',
      borderRadius: 12,
      border: `1.5px solid ${theme.palette.divider}`,
      cursor: 'pointer',
      textAlign: 'left',
      width: '100%',
      background: 'none',
      font: 'inherit',
      color: 'inherit',
      transition: theme.transitions.create(['border-color', 'background-color'], {
        duration: theme.transitions.duration.shortest,
      }),
      '&:hover': { borderColor: theme.palette.primary.main },
      '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
    },
    optionCardSelected: {
      borderColor: theme.palette.primary.main,
      backgroundColor: tokens(theme).selectedOptionBG,
    },
    optionCardDisabled: {
      cursor: 'not-allowed',
      opacity: theme.palette.action.disabledOpacity,
      '&:hover': { borderColor: theme.palette.divider },
    },
    optionIcon: {
      width: 36,
      height: 36,
      flex: '0 0 auto',
      borderRadius: 10,
      display: 'grid',
      placeItems: 'center',
      color: theme.palette.primary.main,
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
    },
    optionIconSelected: {
      color: theme.palette.background.paper,
      backgroundColor: theme.palette.primary.main,
    },
    optionTitle: {
      fontSize: 15,
      fontWeight: 600,
      lineHeight: '22px',
      color: theme.palette.text.primary,
    },
    typeGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: 12,
      [theme.breakpoints.down('xs')]: { gridTemplateColumns: '1fr' },
    },
    typeCard: {
      flexDirection: 'column',
      padding: 18,
    },
    pillRow: { marginTop: 4 },
    presetRow: {
      display: 'flex',
      gap: 8,
      marginBottom: 20,
      flexWrap: 'wrap',
    },
    preset: {
      flex: '1 1 140px',
      justifyContent: 'center',
      alignItems: 'center',
      textAlign: 'center',
      padding: '10px 12px',
      fontWeight: 600,
      fontSize: 14,
    },
    toggleRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 16,
      padding: '12px 0',
      borderTop: `1px solid ${theme.palette.divider}`,
      '&:first-of-type': { borderTop: 'none' },
    },
    sensitiveTitle: {
      color: tokens(theme).pillWarnText,
    },
    checkRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '6px 0',
      borderBottom: `1px solid ${theme.palette.divider}`,
      cursor: 'pointer',
    },
    summaryBox: {
      padding: '12px 16px',
      borderRadius: 12,
      backgroundColor: theme.palette.background.default,
      border: `1px solid ${theme.palette.divider}`,
      fontSize: 14,
      lineHeight: '20px',
      marginBottom: 16,
      color: theme.palette.text.primary,
    },
    peopleList: {
      maxHeight: 260,
      overflowY: 'auto',
      margin: '12px 0',
    },
    chips: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 8,
    },
    // Selected person / qualification chip — readable in both themes
    chip: {
      height: 28,
      borderRadius: 8,
      fontSize: 13,
      fontWeight: 500,
      color: theme.palette.text.primary,
      backgroundColor: alpha(theme.palette.primary.main, 0.16),
      border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
      '&:hover, &:focus': { backgroundColor: alpha(theme.palette.primary.main, 0.24) },
      '& .MuiChip-deleteIcon': { color: theme.palette.primary.main, width: 16, height: 16 },
    },
    chipIdle: {
      color: theme.palette.text.secondary,
      backgroundColor: 'transparent',
      borderColor: theme.palette.divider,
      '&:hover, &:focus': { backgroundColor: theme.palette.action.hover },
    },
    empty: {
      textAlign: 'center',
      padding: '20px 0',
      fontSize: 14,
      color: theme.palette.text.secondary,
    },
    alert: {
      marginTop: 16,
      padding: '12px 16px',
      borderRadius: 12,
      backgroundColor: tokens(theme).pillErrorBG,
      color: tokens(theme).pillErrorText,
    },
    alertTitle: { fontWeight: 600, fontSize: 14 },
    alertText: { fontSize: 13, marginTop: 2 },
    note: {
      marginTop: 12,
      fontSize: 13,
      lineHeight: '18px',
      color: theme.palette.text.secondary,
    },
    statusPanel: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      padding: '24px 8px 8px',
      gap: 8,
    },
    statusIcon: {
      width: 48,
      height: 48,
      borderRadius: '50%',
      display: 'grid',
      placeItems: 'center',
      marginBottom: 8,
    },
    // The report mark: the Vyce loader that settles into the check
    statusMark: {
      width: 96,
      height: 96,
      marginBottom: 12,
    },
    // Copy under the mark; a new key on each state replays the fade
    statusCopy: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 8,
      animation: '$copyIn 220ms cubic-bezier(.2,.8,.2,1)',
      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
    },
    '@keyframes copyIn': {
      from: { opacity: 0, transform: 'translateY(6px)' },
      to: { opacity: 1, transform: 'none' },
    },
    statusIconFailed: {
      backgroundColor: tokens(theme).pillErrorBG,
      color: tokens(theme).pillErrorText,
    },
    statusTitle: {
      fontSize: 17,
      fontWeight: 600,
      color: theme.palette.text.primary,
    },
    // Reports history page
    page: {
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
    },
    pageHead: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 16,
      flexWrap: 'wrap',
    },
    pageTitle: { fontSize: 20, fontWeight: 600, color: theme.palette.text.primary },
    card: {
      backgroundColor: theme.palette.background.paper,
      borderRadius: 16,
      padding: '16px 20px',
      display: 'flex',
      alignItems: 'center',
      gap: 16,
      flexWrap: 'wrap',
      border: `1px solid ${theme.palette.divider}`,
    },
    // Stacked card: the report details page and the empty state
    stackCard: {
      backgroundColor: theme.palette.background.paper,
      borderRadius: 16,
      padding: 24,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: 16,
      border: `1px solid ${theme.palette.divider}`,
    },
    emptyCard: {
      alignItems: 'center',
      textAlign: 'center',
      padding: '40px 24px',
      gap: 4,
    },
    cardMain: { flex: '1 1 320px', minWidth: 0 },
    cardTitle: {
      fontSize: 15,
      fontWeight: 600,
      color: theme.palette.text.primary,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    cardMeta: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '4px 12px',
      marginTop: 4,
      fontSize: 13,
      color: theme.palette.text.secondary,
    },
    cardActions: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      flexWrap: 'wrap',
    },
    detailGrid: {
      display: 'grid',
      gridTemplateColumns: '180px 1fr',
      gap: '10px 16px',
      fontSize: 14,
      [theme.breakpoints.down('xs')]: { gridTemplateColumns: '1fr', gap: 4 },
    },
    detailLabel: { color: theme.palette.text.secondary },
    detailValue: { color: theme.palette.text.primary, wordBreak: 'break-word' },
  })
);
